import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import MfaSettings from '../components/MfaSettings';
import { getSettings, updateSettings, getAccounts, createAccount, getVendors, createVendor } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Buildings,
  ListDashes,
  Users,
  Plus,
  Pencil,
  Trash
} from '@phosphor-icons/react';

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [isVendorDialogOpen, setIsVendorDialogOpen] = useState(false);
  
  // Form states
  const [newAccount, setNewAccount] = useState({
    code: '',
    name: '',
    type: 'expense',
    description: ''
  });
  
  const [newVendor, setNewVendor] = useState({
    name: '',
    default_account_id: '',
    is_employee: false,
    is_owner: false,
    is_intercompany: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, accountsRes, vendorsRes] = await Promise.all([
        getSettings(),
        getAccounts(),
        getVendors()
      ]);
      setSettings(settingsRes.data);
      setAccounts(accountsRes.data.accounts);
      setVendors(vendorsRes.data.vendors);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      await createAccount(newAccount);
      toast.success('Account added');
      setIsAccountDialogOpen(false);
      setNewAccount({ code: '', name: '', type: 'expense', description: '' });
      const res = await getAccounts();
      setAccounts(res.data.accounts);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add account');
    }
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    try {
      await createVendor(newVendor);
      toast.success('Vendor added');
      setIsVendorDialogOpen(false);
      setNewVendor({ name: '', default_account_id: '', is_employee: false, is_owner: false, is_intercompany: false });
      const res = await getVendors();
      setVendors(res.data.vendors);
    } catch (error) {
      toast.error('Failed to add vendor');
    }
  };

  if (loading) {
    return (
      <Layout title="Settings">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Settings">
      <div data-testid="settings-page">
        <Tabs defaultValue="company" className="space-y-6">
          <TabsList className="bg-zinc-100 p-1 rounded-sm">
            <TabsTrigger value="company" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-company">
              <Buildings size={16} className="mr-2" />
              Company
            </TabsTrigger>
            <TabsTrigger value="accounts" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-accounts">
              <ListDashes size={16} className="mr-2" />
              Chart of Accounts
            </TabsTrigger>
            <TabsTrigger value="vendors" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-vendors">
              <Users size={16} className="mr-2" />
              Vendors
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-security">
              Security
            </TabsTrigger>
          </TabsList>

          {/* Company Settings */}
          <TabsContent value="company">
            <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-6">
              <div>
                <h3 className="font-semibold mb-4">Business Context</h3>
                <p className="text-sm text-zinc-500 mb-6">
                  These settings help the AI understand your business context for better classification.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Industry</Label>
                  <Select
                    value={settings.industry || ''}
                    onValueChange={(v) => setSettings({ ...settings, industry: v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technology">Technology / Software</SelectItem>
                      <SelectItem value="retail">Retail / E-commerce</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="professional_services">Professional Services</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="hospitality">Hospitality / Food Service</SelectItem>
                      <SelectItem value="real_estate">Real Estate</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Entity Type</Label>
                  <Select
                    value={settings.entity_type || ''}
                    onValueChange={(v) => setSettings({ ...settings, entity_type: v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="entity-type-select">
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="llc">LLC</SelectItem>
                      <SelectItem value="s_corp">S Corporation</SelectItem>
                      <SelectItem value="c_corp">C Corporation</SelectItem>
                      <SelectItem value="nonprofit">Non-profit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Accounting Basis</Label>
                  <Select
                    value={settings.accounting_basis || ''}
                    onValueChange={(v) => setSettings({ ...settings, accounting_basis: v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="accounting-basis-select">
                      <SelectValue placeholder="Select basis" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash Basis</SelectItem>
                      <SelectItem value="accrual">Accrual Basis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Fiscal Year End</Label>
                  <Select
                    value={settings.fiscal_year_end || ''}
                    onValueChange={(v) => setSettings({ ...settings, fiscal_year_end: v })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="fiscal-year-select">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="january">January</SelectItem>
                      <SelectItem value="february">February</SelectItem>
                      <SelectItem value="march">March</SelectItem>
                      <SelectItem value="april">April</SelectItem>
                      <SelectItem value="may">May</SelectItem>
                      <SelectItem value="june">June</SelectItem>
                      <SelectItem value="july">July</SelectItem>
                      <SelectItem value="august">August</SelectItem>
                      <SelectItem value="september">September</SelectItem>
                      <SelectItem value="october">October</SelectItem>
                      <SelectItem value="november">November</SelectItem>
                      <SelectItem value="december">December</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Materiality Threshold ($)</Label>
                  <Input
                    type="number"
                    value={settings.materiality_threshold || ''}
                    onChange={(e) => setSettings({ ...settings, materiality_threshold: parseFloat(e.target.value) || null })}
                    placeholder="e.g., 5000"
                    className="rounded-sm font-mono"
                    data-testid="materiality-threshold-input"
                  />
                  <p className="text-xs text-zinc-400">Transactions above this amount require additional review</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Departments (comma-separated)</Label>
                  <Input
                    value={settings.departments?.join(', ') || ''}
                    onChange={(e) => setSettings({ ...settings, departments: e.target.value.split(',').map(d => d.trim()).filter(Boolean) })}
                    placeholder="e.g., Sales, Engineering, Marketing"
                    className="rounded-sm"
                    data-testid="departments-input"
                  />
                </div>
              </div>

              <div className="pt-6 border-t border-zinc-200">
                <Button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm"
                  data-testid="save-settings-button"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Chart of Accounts */}
          <TabsContent value="accounts">
            <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Chart of Accounts</h3>
                  <p className="text-sm text-zinc-500">{accounts.length} accounts</p>
                </div>
                <Dialog open={isAccountDialogOpen} onOpenChange={setIsAccountDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="add-account-button">
                      <Plus size={18} className="mr-2" />
                      Add Account
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-account-dialog">
                    <DialogHeader>
                      <DialogTitle>Add Account</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddAccount} className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-widest text-zinc-500">Code</Label>
                          <Input
                            value={newAccount.code}
                            onChange={(e) => setNewAccount({ ...newAccount, code: e.target.value })}
                            placeholder="e.g., 6100"
                            required
                            className="rounded-sm font-mono"
                            data-testid="account-code-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-widest text-zinc-500">Type</Label>
                          <Select value={newAccount.type} onValueChange={(v) => setNewAccount({ ...newAccount, type: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="account-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="asset">Asset</SelectItem>
                              <SelectItem value="liability">Liability</SelectItem>
                              <SelectItem value="equity">Equity</SelectItem>
                              <SelectItem value="revenue">Revenue</SelectItem>
                              <SelectItem value="expense">Expense</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label>
                        <Input
                          value={newAccount.name}
                          onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                          placeholder="e.g., Office Supplies"
                          required
                          className="rounded-sm"
                          data-testid="account-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
                        <Input
                          value={newAccount.description}
                          onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
                          placeholder="Optional description"
                          className="rounded-sm"
                          data-testid="account-description-input"
                        />
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsAccountDialogOpen(false)} className="rounded-sm">
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="submit-account-button">
                          Add Account
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {accounts.length === 0 ? (
                <div className="p-12 text-center text-zinc-500">
                  <ListDashes size={48} className="mx-auto mb-4 text-zinc-300" />
                  <p>No accounts yet. Add your first account.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full table-dense">
                    <thead>
                      <tr className="text-left">
                        <th className="px-6 pt-4">Code</th>
                        <th className="px-6 pt-4">Name</th>
                        <th className="px-6 pt-4">Type</th>
                        <th className="px-6 pt-4">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((acc) => (
                        <tr key={acc.account_id} className="hover:bg-zinc-50 transition-colors duration-150">
                          <td className="px-6 font-mono text-sm">{acc.code}</td>
                          <td className="px-6 text-sm font-medium">{acc.name}</td>
                          <td className="px-6">
                            <span className="px-2 py-1 text-xs uppercase tracking-wider bg-zinc-100 rounded-sm">
                              {acc.type}
                            </span>
                          </td>
                          <td className="px-6 text-sm text-zinc-500">{acc.description || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Vendors */}
          <TabsContent value="vendors">
            <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Vendors</h3>
                  <p className="text-sm text-zinc-500">{vendors.length} vendors</p>
                </div>
                <Dialog open={isVendorDialogOpen} onOpenChange={setIsVendorDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="add-vendor-button">
                      <Plus size={18} className="mr-2" />
                      Add Vendor
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-vendor-dialog">
                    <DialogHeader>
                      <DialogTitle>Add Vendor</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddVendor} className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-zinc-500">Vendor Name</Label>
                        <Input
                          value={newVendor.name}
                          onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                          placeholder="e.g., Amazon Web Services"
                          required
                          className="rounded-sm"
                          data-testid="vendor-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-zinc-500">Default Account</Label>
                        <Select
                          value={newVendor.default_account_id || ''}
                          onValueChange={(v) => setNewVendor({ ...newVendor, default_account_id: v })}
                        >
                          <SelectTrigger className="rounded-sm" data-testid="vendor-account-select">
                            <SelectValue placeholder="Select default account" />
                          </SelectTrigger>
                          <SelectContent>
                            {accounts.map((acc) => (
                              <SelectItem key={acc.account_id} value={acc.account_id}>
                                {acc.code} - {acc.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-4 pt-2">
                        <p className="text-xs uppercase tracking-widest text-zinc-500">Sensitive Flags</p>
                        <p className="text-xs text-zinc-400 -mt-2">
                          Transactions with these vendors will be flagged for review
                        </p>
                        
                        <div className="flex items-center justify-between py-2">
                          <Label className="text-sm">Is Employee (Payroll)</Label>
                          <Switch
                            checked={newVendor.is_employee}
                            onCheckedChange={(checked) => setNewVendor({ ...newVendor, is_employee: checked })}
                            data-testid="vendor-is-employee-switch"
                          />
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <Label className="text-sm">Is Owner</Label>
                          <Switch
                            checked={newVendor.is_owner}
                            onCheckedChange={(checked) => setNewVendor({ ...newVendor, is_owner: checked })}
                            data-testid="vendor-is-owner-switch"
                          />
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <Label className="text-sm">Is Intercompany</Label>
                          <Switch
                            checked={newVendor.is_intercompany}
                            onCheckedChange={(checked) => setNewVendor({ ...newVendor, is_intercompany: checked })}
                            data-testid="vendor-is-intercompany-switch"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsVendorDialogOpen(false)} className="rounded-sm">
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="submit-vendor-button">
                          Add Vendor
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {vendors.length === 0 ? (
                <div className="p-12 text-center text-zinc-500">
                  <Users size={48} className="mx-auto mb-4 text-zinc-300" />
                  <p>No vendors yet. Add your first vendor.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full table-dense">
                    <thead>
                      <tr className="text-left">
                        <th className="px-6 pt-4">Name</th>
                        <th className="px-6 pt-4">Default Account</th>
                        <th className="px-6 pt-4">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors.map((vendor) => (
                        <tr key={vendor.vendor_id} className="hover:bg-zinc-50 transition-colors duration-150">
                          <td className="px-6 text-sm font-medium">{vendor.name}</td>
                          <td className="px-6 text-sm text-zinc-600 font-mono">
                            {vendor.default_account_id || '—'}
                          </td>
                          <td className="px-6">
                            <div className="flex gap-2">
                              {vendor.is_employee && (
                                <span className="px-2 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-sm">
                                  Employee
                                </span>
                              )}
                              {vendor.is_owner && (
                                <span className="px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded-sm">
                                  Owner
                                </span>
                              )}
                              {vendor.is_intercompany && (
                                <span className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-sm">
                                  Intercompany
                                </span>
                              )}
                              {!vendor.is_employee && !vendor.is_owner && !vendor.is_intercompany && (
                                <span className="text-xs text-zinc-400">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Security / MFA */}
          <TabsContent value="security">
            <MfaSettings />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
